import styles from "./addExistingUserModalFirstStep.module.scss";
import { useTranslation } from "react-i18next";
import { useGetUserProjectsQuery } from "../../../api/services/project.service";
import { useLazyGetUsersToAssignQuery } from "../../../api/services/user.service";
import {
  number,
  object
} from "yup";
import {
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import { SearchInput } from "../../../shared/components/searchInput/searchInput";
import { UserToAssign } from "../../../types/user.types";
import { Checkbox } from "../../../shared/components/checkbox/checkbox";
import { setShowSpinner } from "../../../store/slices/mainSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { Project } from "../../../types/project.types";

type AddExistingUserModalFirstStepProps = {
  selectedUsers: Array<UserToAssign>;
  onChangeSelectedUsers: (users: Array<UserToAssign>) => void;
  selectedProject?: Project;
  onChangeProject: (project: Project) => void;
}

export const AddExistingUserModalFirstStep = ({ selectedUsers, onChangeSelectedUsers, selectedProject, onChangeProject }: AddExistingUserModalFirstStepProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredUsers, setFilteredUsers] = useState<Array<UserToAssign>>([]);

  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const [getUsersToAssign, { data: usersToAssign, isLoading: isLoadingUsers, isFetching }] = useLazyGetUsersToAssignQuery()
  const { data: userProjects } = useGetUserProjectsQuery("");

  const initialSchema = object({
    projectId: number().required(t("common.notValid"))
  });
  const {
    control,
  } = useForm({
    resolver: yupResolver(initialSchema),
    mode: "onChange",
    defaultValues: {
      projectId: selectedProject?.id || userProjects?.[0]?.id || 0
    }
  });
  const projectIdWatch = useWatch({ control, name: "projectId" });

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingUsers || isFetching));
  }, [isLoadingUsers, isFetching]);

  useEffect(() => {
    if (!!projectIdWatch) {
      getUsersToAssign(projectIdWatch);
      const project = userProjects?.find(item => item.id === projectIdWatch);
      if (project) {
        onChangeProject(project);
      }
    }
  }, [projectIdWatch, getUsersToAssign]);

  useEffect(() => {
    if (!!usersToAssign) {
      const needClean = selectedUsers.some(user => {
        return !usersToAssign.some(item => item.id === user.id)
      });
      if (needClean) {
        onChangeSelectedUsers([]);
      }
    }
  }, [usersToAssign]);

  useEffect(() => {
    if (!!usersToAssign) {
      setFilteredUsers(usersToAssign.filter(item =>
        item.fullName.toLowerCase().includes(searchValue.toLowerCase())
        || item.email.toLowerCase().includes(searchValue.toLowerCase())
      ))
    }
  }, [usersToAssign, searchValue]);

  useEffect(() => {
    const newIsSelectAll = !filteredUsers.some(user => !selectedUsers.find(item => item.id === user.id))
      setIsSelectAll(newIsSelectAll);
  }, [selectedUsers, filteredUsers]);

  const onChangeSearch = (value: string): void => {
    setSearchValue(value);
  }

  const onClickSelectAll = (): void => {
    const newSelectedUsers = [...selectedUsers];
    if (isSelectAll) {
      filteredUsers.forEach(user => {
        const findIndex = newSelectedUsers.findIndex(item => item.id === user.id);
        if (findIndex > -1) {
          newSelectedUsers.splice(findIndex, 1);
        }
      });
    } else {
      filteredUsers.forEach(user => {
        const find = newSelectedUsers.find(item => item.id === user.id);
        if (!find) {
          newSelectedUsers.push(user);
        }
      });
    }
    onChangeSelectedUsers(newSelectedUsers);
  }

  const onClickUserCheckbox = (user: UserToAssign) => {
    const newSelectedUser = [...selectedUsers];
    const findIndex = newSelectedUser.findIndex(item => item.id === user.id);
    if (findIndex > -1) {
      newSelectedUser.splice(findIndex, 1);
    } else {
      newSelectedUser.push(user);
    }
    onChangeSelectedUsers(newSelectedUser)
  }

  const projectDropdownItems: Array<DropdownItem> = useMemo(() => {
    return userProjects?.map(project => ({
      id: project.id,
      name: project.name,
      title: project.name
    })) || [];
  }, [userProjects]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.inputContainer}>
          <div className={styles.label}>{t("main.project")}</div>
          <div className={styles.projectsDropdown}>
            <Dropdown items={projectDropdownItems}
                      name={"projectId"}
                      control={control}/>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.search}>
            <SearchInput value={searchValue} onChange={onChangeSearch}/>
          </div>
          <div className={styles.checkbox} onClick={onClickSelectAll}>
            {t("admin.selectAll")}
            <Checkbox isChecked={isSelectAll}/>
          </div>
        </div>
      </div>
      <div className={styles.users}>
        {filteredUsers.map((user, index) => {
          const isSelected = selectedUsers.includes(user);
          return (
            <div className={`${styles.user} ${isSelected ? styles.selected : ""}`} key={index}>
              <div className={styles.name}>{user.fullName}</div>
              <div className={styles.email}>{user.email}</div>
              <Checkbox isChecked={isSelected} onChange={() => onClickUserCheckbox(user)}/>
            </div>
          )
        })}
      </div>
    </div>
  );
}